import { Button, Switch, Textarea } from '@h2hspark/ui';
import { Check } from 'lucide-react';
import * as React from 'react';

import {
  CAPTURED_SUBHEADING,
  CAPTURE_SAVE_LABEL,
  CAPTURE_SKIP_HINT,
  CAPTURE_SKIP_LABEL,
  FOLLOW_UP_INTENT_LABEL,
  QUICK_NOTE_LABEL,
  QUICK_NOTE_MAX_LENGTH,
  QUICK_NOTE_PLACEHOLDER,
  QUICK_NOTE_PRIVACY_HINT,
  capturedHeading,
  type ScanStage,
} from './constants';

/**
 * The quick note and the follow-up intent (`MEM-029` AC-3, AC-4).
 *
 * ## It renders *after* the connection exists, and that is the whole design
 *
 * `MEM-029` AC-3 says *"immediately **after** logging"*, and the story's note explains the stake:
 * *"abandoning the prompt costs nothing. Asking first would make the fast path slow to protect
 * data nobody has to give."*
 *
 * That ordering is structural here rather than remembered. This component is rendered only for
 * `stage.kind === 'captured'`, and `captured` is only reachable from an outcome that carried a
 * connection (`nextScanStage`) — so there is no code path on which this appears before the record
 * exists, and none on which dismissing it removes one.
 *
 * The heading is in the **past tense** for the same reason: "Connected with Priya", not "Log this
 * connection?". A prompt that reads like a step is a step, whatever the code does.
 *
 * ## Both controls are optional and the screen says so
 *
 * `MEM-029` AC-4. The dismissal is "Skip for now", not "Cancel" — cancelling implies undoing what
 * just happened, and there is nothing to undo (`MEM-031` AC-4). The line under it says where the
 * hesitation is: *"Either way, they are already in your connections."*
 *
 * ## What is not here
 *
 * **No remove, no "not this person", no undo.** `MEM-031` AC-4, and `constants.ts`'s
 * `NO_UNDO_IS_NOT_BUILT` says why the affordance is absent rather than disabled: removal exists,
 * but it is `MEM-034`'s Disconnect, on a screen that states the consequence before the tap.
 */
export function CaptureDialog({
  stage,
  busy,
  onSave,
  onSkip,
}: {
  readonly stage: ScanStage;
  readonly busy: boolean;
  readonly onSave: (input: {
    readonly attendeeRef: string;
    readonly note?: string;
    readonly followUpIntent?: boolean;
  }) => void;
  readonly onSkip: () => void;
}): React.ReactElement | null {
  const [note, setNote] = React.useState('');
  const [followUp, setFollowUp] = React.useState(false);

  if (stage.kind !== 'captured') return null;
  const { attendeeRef, firstName, alreadyConnected } = stage;

  const save = (): void => {
    const trimmed = note.trim();
    onSave({
      attendeeRef,
      // Only what the member actually gave. An empty note is not a note, and sending one would
      // overwrite whatever they wrote a minute ago on a second scan of the same person.
      ...(trimmed.length === 0 ? {} : { note: trimmed }),
      ...(followUp ? { followUpIntent: true } : {}),
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={capturedHeading(firstName)}
      className="fixed inset-0 z-50 flex w-full flex-col bg-background"
    >
      <header className="flex-none space-y-1 border-b border-border-subtle px-5 pb-4 pt-12 text-center">
        <span
          className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-pill bg-success-subtle text-success"
          aria-hidden
        >
          <Check className="h-6 w-6" />
        </span>
        <h2 className="font-brand text-lg font-extrabold text-foreground">
          {capturedHeading(firstName)}
        </h2>
        {/*
          A second scan of the same person is not a failure and not a new record — it says so
          plainly rather than pretending this tap did it. `MEM-031` AC-4's no-undo is what makes a
          repeated tap safe, and this line is how the member knows it landed.
        */}
        <p className="text-sm text-muted-foreground">
          {alreadyConnected ? `You were already connected with ${firstName}.` : CAPTURED_SUBHEADING}
        </p>
      </header>

      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="h2h-eyebrow">{QUICK_NOTE_LABEL}</span>
            {/* The note is the member's own record of a conversation and reaches nobody else —
                including its subject (`MEM-029` AC-5). Saying so where they type it is the point
                at which that promise is worth anything. */}
            <span className="text-2xs text-text-subtle">{QUICK_NOTE_PRIVACY_HINT}</span>
          </div>
          <Textarea
            value={note}
            maxLength={QUICK_NOTE_MAX_LENGTH}
            placeholder={QUICK_NOTE_PLACEHOLDER}
            aria-label={QUICK_NOTE_LABEL}
            className="min-h-28"
            onChange={(changed) => {
              setNote(changed.target.value);
            }}
          />
        </div>

        <label className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-card p-3.5">
          <span className="font-brand text-sm font-bold text-foreground">
            {FOLLOW_UP_INTENT_LABEL}
          </span>
          <Switch checked={followUp} onCheckedChange={setFollowUp} />
        </label>
      </div>

      <div className="flex-none space-y-2 border-t border-border-subtle px-5 pb-8 pt-4">
        <Button className="w-full" size="lg" disabled={busy} onClick={save}>
          {CAPTURE_SAVE_LABEL}
        </Button>
        <Button className="w-full" variant="ghost" disabled={busy} onClick={onSkip}>
          {CAPTURE_SKIP_LABEL}
        </Button>
        <p className="text-center text-xs text-muted-foreground">{CAPTURE_SKIP_HINT}</p>
      </div>
    </div>
  );
}
